import styled from '@emotion/styled';
import { Box, IconButton, TextField, Tooltip } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ResetButton } from './ResetButton';
import { StyledInputLabel } from '../../../../../components/inputs/InputLabel';
import { StyledQuestionMark } from '../../../../../components/QuestionMark';
import { translateFieldError } from '../../../../../utils/apiErrors';

// Create a custom input since the original is used everywhere and has margin embedded in it
// && needed for higher specificity
const NoMarginInputLabel = styled(StyledInputLabel)`
  && {
    margin-bottom: 0;
  }
`;
export const TextFieldWithHelp = memo(
  ({
    name,
    label,
    type = 'text',
    value,
    onChange,
    disabled,
    error,
    errorText,
    helpText,
    onReset,
    fullWidth = true,
    required = false,
    lang,
  }: {
    name: string;
    label: string;
    type?: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    disabled: boolean;
    error?: boolean;
    errorText?: string;
    helpText?: string;
    onReset?: () => void;
    fullWidth?: boolean;
    margin?: 'normal' | 'dense' | 'none';
    required?: boolean;
    lang?: string;
  }) => {
    const { t } = useTranslation();

    return (
      <Box sx={{ my: 14 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <NoMarginInputLabel htmlFor={name}>{label}</NoMarginInputLabel>
          {helpText && (
            <Tooltip title={helpText} placement="top">
              <IconButton size="small">
                <StyledQuestionMark fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <TextField
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          fullWidth={fullWidth}
          margin="none"
          disabled={disabled}
          error={error}
          helperText={errorText ? translateFieldError(t, errorText) : undefined}
          required={required}
          placeholder={label}
          inputProps={lang ? { lang } : undefined}
          InputProps={{
            endAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {onReset && (
                  <ResetButton
                    value={value}
                    onReset={onReset}
                    disabled={disabled}
                  />
                )}
              </Box>
            ),
          }}
        />
      </Box>
    );
  }
);

TextFieldWithHelp.displayName = 'TextFieldWithHelp';
